import { useEffect, useState } from "react";
import { api, BackfillEntry } from "../api";
import {
  GlassPanel,
  KpiCard,
  Badge,
  Modal,
  DataTable,
  Column,
  SkeletonTable,
  ErrorState,
  toast,
} from "../components";
import { formatVND, formatNumber, formatDate } from "../utils";

type TabKey = "all" | "pending_approval" | "approved" | "rejected";

const REASON_LABELS: Record<string, string> = {
  damaged: "Hỏng",
  lost: "Mất",
  returned: "Trả lại",
  counted_wrong: "Đếm sai",
  other: "Khác",
};

const STATUS_LABELS: Record<string, string> = {
  pending_approval: "Chờ duyệt",
  approved: "Đã duyệt",
  rejected: "Bị từ chối",
};

const STATUS_VARIANT: Record<string, "warn" | "ok" | "bad"> = {
  pending_approval: "warn",
  approved: "ok",
  rejected: "bad",
};

const TABS: { key: TabKey; label: string }[] = [
  { key: "all", label: "Tất cả" },
  { key: "pending_approval", label: "Chờ duyệt" },
  { key: "approved", label: "Đã duyệt" },
  { key: "rejected", label: "Bị từ chối" },
];

export default function BackfillPage() {
  const [entries, setEntries] = useState<BackfillEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("all");
  const [showAdd, setShowAdd] = useState(false);
  const [showApprove, setShowApprove] = useState<BackfillEntry | null>(null);

  const [formName, setFormName] = useState("");
  const [formSku, setFormSku] = useState("");
  const [formExpected, setFormExpected] = useState("");
  const [formActual, setFormActual] = useState("");
  const [formReason, setFormReason] = useState<BackfillEntry["reason"]>("damaged");
  const [formNote, setFormNote] = useState("");

  const [approverName, setApproverName] = useState("");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.backfill.list();
      setEntries(r.entries || []);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = tab === "all" ? entries : entries.filter((e) => e.status === tab);

  const totalLoss = entries
    .filter((e) => e.delta < 0)
    .reduce((sum, e) => sum + Math.abs(e.delta) * 1000, 0);
  const pendingCount = entries.filter((e) => e.status === "pending_approval").length;

  const handleAdd = async () => {
    const expected = Number(formExpected) || 0;
    const actual = Number(formActual) || 0;
    if (!formName.trim()) return;
    try {
      await api.backfill.create({
        item_name: formName.trim(),
        sku: formSku.trim() || undefined,
        expected_qty: expected,
        actual_qty: actual,
        reason: formReason,
        note: formNote.trim() || undefined,
      });
      toast.success("Đã thêm điều chỉnh");
      resetForm();
      setShowAdd(false);
      load();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  const resetForm = () => {
    setFormName("");
    setFormSku("");
    setFormExpected("");
    setFormActual("");
    setFormReason("damaged");
    setFormNote("");
  };

  const handleApprove = async () => {
    if (!showApprove || !approverName.trim()) return;
    try {
      await api.backfill.approve(showApprove.id, approverName.trim());
      toast.success("Đã duyệt");
      setShowApprove(null);
      setApproverName("");
      load();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  const handleReject = async (id: string) => {
    try {
      await api.backfill.update(id, { status: "rejected" });
      toast.success("Đã từ chối");
      load();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  const columns: Column<BackfillEntry>[] = [
    {
      key: "date",
      header: "Ngày",
      render: (r) => <span style={{ fontSize: 12 }}>{formatDate(r.created_at)}</span>,
    },
    {
      key: "item",
      header: "Sản phẩm",
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.item_name}</div>
          {r.sku && <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{r.sku}</div>}
        </div>
      ),
    },
    {
      key: "expected",
      header: "Dự kiến",
      render: (r) => <span style={{ textAlign: "right", display: "block" }}>{formatNumber(r.expected_qty)}</span>,
    },
    {
      key: "actual",
      header: "Thực tế",
      render: (r) => <span style={{ textAlign: "right", display: "block" }}>{formatNumber(r.actual_qty)}</span>,
    },
    {
      key: "delta",
      header: "Chênh lệch",
      render: (r) => (
        <span
          style={{
            textAlign: "right",
            display: "block",
            fontWeight: 700,
            color: r.delta < 0 ? "var(--accent-red)" : r.delta > 0 ? "var(--accent-green)" : undefined,
          }}
        >
          {r.delta > 0 ? `+${r.delta}` : r.delta}
        </span>
      ),
    },
    {
      key: "reason",
      header: "Lý do",
      render: (r) => <span>{REASON_LABELS[r.reason] || r.reason}</span>,
    },
    {
      key: "status",
      header: "Trạng thái",
      render: (r) => <Badge variant={STATUS_VARIANT[r.status]}>{STATUS_LABELS[r.status]}</Badge>,
    },
    {
      key: "actions",
      header: "Hành động",
      render: (r) =>
        r.status === "pending_approval" ? (
          <div style={{ display: "flex", gap: 4 }}>
            <button
              className="btn btn-sm btn-ghost"
              title="Phê duyệt"
              onClick={() => { setShowApprove(r); setApproverName(""); }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--accent-green)" }}>check</span>
            </button>
            <button
              className="btn btn-sm btn-ghost"
              title="Từ chối"
              onClick={() => handleReject(r.id)}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--accent-red)" }}>close</span>
            </button>
          </div>
        ) : (
          <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
            {r.approver ? `Duyệt: ${r.approver}` : "—"}
          </span>
        ),
    },
  ];

  return (
    <>
      {/* KPI Cards */}
      <div className="kpi-grid">
        <KpiCard
          label="Giá trị lỗ (ước tính)"
          value={formatVND(totalLoss)}
          dotColor="bad"
          note="tổng chênh lệch âm"
        />
        <KpiCard
          label="Số lượng entries"
          value={formatNumber(entries.length)}
          dotColor="blue"
          note="tổng điều chỉnh"
        />
        <KpiCard
          label="Chờ duyệt"
          value={pendingCount.toString()}
          dotColor={pendingCount > 0 ? "warn" : "ok"}
          note="cần phê duyệt"
        />
      </div>

      {/* Tab nav + Table */}
      <GlassPanel>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", gap: 4 }}>
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`btn btn-sm ${tab === t.key ? "" : "btn-ghost"}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button className="btn" onClick={() => setShowAdd(true)}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
            Thêm điều chỉnh
          </button>
        </div>

        {loading ? (
          <SkeletonTable rows={5} cols={8} />
        ) : error ? (
          <ErrorState error={error} onRetry={load} />
        ) : entries.length === 0 ? (
          <div className="empty" style={{ padding: "48px 16px", textAlign: "center" }}>
            <span className="material-symbols-outlined" style={{ fontSize: 48, opacity: 0.3, marginBottom: 8, display: "block" }}>tune</span>
            <p style={{ color: "var(--text-secondary)" }}>Chưa có điều chỉnh nào. Thêm khi phát hiện chênh lệch tồn kho.</p>
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            emptyMessage="Không có mục nào trong tab này"
          />
        )}
      </GlassPanel>

      {/* Modal Thêm điều chỉnh */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Thêm điều chỉnh tồn kho">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <fieldset style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
            <legend style={{ fontSize: 12, color: "var(--text-tertiary)", padding: "0 4px" }}>Sản phẩm</legend>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <input
                placeholder="Tên sản phẩm *"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
              />
              <input
                placeholder="SKU / Barcode"
                value={formSku}
                onChange={(e) => setFormSku(e.target.value)}
              />
            </div>
          </fieldset>

          <fieldset style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
            <legend style={{ fontSize: 12, color: "var(--text-tertiary)", padding: "0 4px" }}>Số lượng</legend>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Hệ thống ghi</label>
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="0"
                  value={formExpected}
                  onChange={(e) => setFormExpected(e.target.value)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Thực tế</label>
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="0"
                  value={formActual}
                  onChange={(e) => setFormActual(e.target.value)}
                />
              </div>
            </div>
            {formExpected && formActual && (
              <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: Number(formActual) - Number(formExpected) < 0 ? "var(--accent-red)" : "var(--accent-green)" }}>
                Chênh lệch: {Number(formActual) - Number(formExpected) > 0 ? "+" : ""}{Number(formActual) - Number(formExpected)}
              </div>
            )}
          </fieldset>

          <fieldset style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
            <legend style={{ fontSize: 12, color: "var(--text-tertiary)", padding: "0 4px" }}>Lý do</legend>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <select value={formReason} onChange={(e) => setFormReason(e.target.value as BackfillEntry["reason"])}>
                <option value="damaged">Hỏng</option>
                <option value="lost">Mất</option>
                <option value="returned">Trả lại</option>
                <option value="counted_wrong">Đếm sai</option>
                <option value="other">Khác</option>
              </select>
              <input
                placeholder="Ghi chú thêm (tuỳ chọn)"
                value={formNote}
                onChange={(e) => setFormNote(e.target.value)}
              />
            </div>
          </fieldset>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={() => setShowAdd(false)}>Huỷ</button>
          <button className="btn" onClick={handleAdd} disabled={!formName.trim()}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
            Thêm
          </button>
        </div>
      </Modal>

      {/* Modal Phê duyệt */}
      <Modal
        open={!!showApprove}
        onClose={() => setShowApprove(null)}
        title="Phê duyệt điều chỉnh"
        width={420}
        footer={
          <button className="btn" onClick={handleApprove} disabled={!approverName.trim()}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>check_circle</span>
            Xác nhận duyệt
          </button>
        }
      >
        {showApprove && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p style={{ fontSize: 13 }}>
              Duyệt điều chỉnh <strong>{showApprove.item_name}</strong>: chênh lệch{" "}
              <strong style={{ color: showApprove.delta < 0 ? "var(--accent-red)" : "var(--accent-green)" }}>
                {showApprove.delta > 0 ? "+" : ""}{showApprove.delta}
              </strong>{" "}
              — Lý do: {REASON_LABELS[showApprove.reason]}
            </p>
            <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Người phê duyệt</label>
            <input
              placeholder="Nhập tên admin phê duyệt"
              value={approverName}
              onChange={(e) => setApproverName(e.target.value)}
              autoFocus
            />
          </div>
        )}
      </Modal>
    </>
  );
}
